import React, { useEffect, useState } from 'react'

import { useLocation, useNavigate, useParams } from 'react-router-dom'

import ChatSection from './ChatSection'
import LinksSection from './LinksSection'
import LogSection from './LogSection'
import RoutineSection from './RoutineSection'
import {
  DetailPerson,
  FilteringList,
  InlineMenuNoButton,
  ListWithSearch,
  SectionSubtitle,
} from '../../..'
import { useStudents, useUser } from '../../../../context'
import { useWindowSize } from '../../../../hooks/useWindowSize'
import {
  AnyProfile,
  ProfesorStudentMenu,
  Student,
  URLNavigation,
} from '../../../../types'
import { Spacer } from '../../../global'
import ProfileView from '../../../profile/ProfileView'
import {
  AdminsContainer,
  DataContainer,
  ProfileDataSection,
} from '../../administration/styles'

import { ProfStudentContent } from './styles'

const ProfStudents = ({ part }: { part: string }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { studentName } = useParams<{ studentName?: string }>()
  const { onEditionMode } = useUser()
  const { activeStudent, setActiveStudent, studentsList } = useStudents()
  const { isMobile } = useWindowSize()

  const [showList, setShowList] = useState<boolean>(true)
  const [searchValue, setSearchValue] = useState<string>('')
  const [filteredStudents, setFilteredStudents] = useState<Student[] | null>(
    null,
  )

  const selectStudent = (student: AnyProfile) => {
    if (!student) return
    setActiveStudent(student as Student)
    const studentSlug = `${student.name.toLowerCase()}-${student.lastname.toLowerCase()}`
    navigate(`${URLNavigation.STUDENTS}/${studentSlug}`)

    if (isMobile) {
      setShowList(false)
    }
  }

  useEffect(() => {
    setFilteredStudents(FilteringList(searchValue, studentsList))
  }, [searchValue, studentsList])

  useEffect(() => {
    if (!studentsList || studentsList.length === 0) return
    if (studentName) {
      const target = decodeURIComponent(studentName).toLowerCase()
      const match = studentsList.find(
        (student) =>
          `${student.name.toLowerCase()}-${student.lastname.toLowerCase()}` ===
          target,
      )
      if (match && activeStudent?.uid !== match.uid) {
        setActiveStudent(match)
      }
      return
    }
    if (activeStudent === null && !onEditionMode) {
      setActiveStudent(studentsList[0])
    }
  }, [
    activeStudent,
    onEditionMode,
    setActiveStudent,
    studentName,
    studentsList,
  ])

  useEffect(() => {
    isMobile && setShowList(false)
  }, [])

  return (
    <AdminsContainer className={`${isMobile && showList ? 'open' : ''}`}>
      <ListWithSearch
        type="alumno"
        searchValue={searchValue}
        filteredStudents={filteredStudents}
        setSearchValue={setSearchValue}
        selectStudent={selectStudent}
        showList={showList}
        setShowList={setShowList}
      />
      <DataContainer>
        {activeStudent && (
          <>
            <InlineMenuNoButton
              menu={ProfesorStudentMenu}
              editing={onEditionMode}
              type={part}
              isTopMenu={true}
              suffix={
                activeStudent
                  ? `${activeStudent.name.toLowerCase()}-${activeStudent.lastname.toLowerCase()}`
                  : studentName
              }
            />
            {part !== ProfesorStudentMenu.profile.label && (
              <ProfileDataSection>
                <DetailPerson person={activeStudent} />
              </ProfileDataSection>
            )}
            {part === ProfesorStudentMenu.profile.label && (
              <div className="section-title">
                <SectionSubtitle fontWeight={600} fontSize={16} lineHeight={24}>
                  <>
                    <Spacer height={16} />
                    {ProfesorStudentMenu.profile.description}

                    <Spacer height={10} />
                  </>
                </SectionSubtitle>
              </div>
            )}
            <Spacer height={32} />

            <ProfStudentContent>
              {part === ProfesorStudentMenu.logs.label && <LogSection />}
              {part === ProfesorStudentMenu.routine.label && <RoutineSection />}
              {part === ProfesorStudentMenu.links.label && <LinksSection />}
              {part === ProfesorStudentMenu.chat.label && <ChatSection />}

              {part === ProfesorStudentMenu.profile.label && (
                <ProfileView
                  initialProfile={activeStudent}
                  isNewUser={pathname === '/nuevo-usuario'}
                />
              )}
            </ProfStudentContent>
          </>
        )}
      </DataContainer>
    </AdminsContainer>
  )
}

export default ProfStudents
